/*
 * Own login (replaces Cognito / Amplify).
 *
 * useAuth() returns { user, signOut } where user.attributes has the same keys the
 * pages already read from Amplify (name, email, 'custom:role'), so pages only
 * swap their import.
 */
import { reactive, computed } from 'vue';
import axios from 'axios';

const TOKEN_KEY = 'cims_token';
const USER_KEY = 'cims_user';
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';

const storage = {
  get(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode: session only */ }
  },
  remove(key) {
    try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
  },
};

const toAmplifyShape = (apiUser) => apiUser && ({
  id: apiUser.id,
  username: apiUser.email,
  attributes: {
    sub: apiUser.id,
    email: apiUser.email,
    name: apiUser.name || apiUser.email,
    'custom:role': apiUser.role,
  },
});

const readStoredUser = () => {
  try { return JSON.parse(storage.get(USER_KEY)); } catch (e) { return null; }
};

const state = reactive({
  token: storage.get(TOKEN_KEY),
  user: toAmplifyShape(readStoredUser()),
});

const saveSession = (token, apiUser) => {
  state.token = token;
  state.user = toAmplifyShape(apiUser);
  storage.set(TOKEN_KEY, token);
  storage.set(USER_KEY, JSON.stringify(apiUser));
};

export const signOut = () => {
  state.token = null;
  state.user = null;
  storage.remove(TOKEN_KEY);
  storage.remove(USER_KEY);
};

const NO_USER = { attributes: {} };

// Same usage as Amplify: const { user, signOut } = toRefs(useAuth());
export const useAuth = () => reactive({
  user: computed(() => state.user || NO_USER),
  signOut,
});

export const isLoggedIn = () => Boolean(state.token && state.user);
export const authState = state;

const errorMessage = (error) => {
  const detail = error.response && error.response.data && error.response.data.detail;
  if (typeof detail === 'string') return detail;
  if (!error.response) return 'Cannot reach the server. Check your connection.';
  return 'Something went wrong. Please try again.';
};

/** Throws an Error whose message is PASSWORD_CHANGE_REQUIRED when the user must set a new password */
export const signIn = async (email, password) => {
  try {
    const { data } = await axios.post('/v1/auth/login', { email, password });
    saveSession(data.access_token, data.user);
  } catch (error) {
    throw new Error(errorMessage(error));
  }
};

export const changePassword = async (email, currentPassword, newPassword) => {
  try {
    const { data } = await axios.post('/v1/auth/change-password', {
      email,
      current_password: currentPassword,
      new_password: newPassword,
    });
    saveSession(data.access_token, data.user);
  } catch (error) {
    throw new Error(errorMessage(error));
  }
};

/** Call once at startup: attaches the token to every request and handles renew / expiry */
export const setupAuth = () => {
  // Old Amplify/Cognito session is no longer used; remove it so nothing reads it
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('CognitoIdentityServiceProvider') || k.startsWith('amplify-'))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* ignore */ }

  axios.interceptors.request.use((config) => {
    if (state.token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${state.token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      // Sliding login: API sends a fresh token when the current one is getting old
      const renewed = response.headers && response.headers['x-renewed-token'];
      if (renewed && state.token) {
        state.token = renewed;
        storage.set(TOKEN_KEY, renewed);
      }
      return response;
    },
    (error) => {
      const url = (error.config && error.config.url) || '';
      if (error.response && error.response.status === 401 && !url.includes('/v1/auth/')) {
        signOut(); // token expired or revoked => back to the login page
      }
      return Promise.reject(error);
    },
  );
};
